import { useEffect, useMemo, useState } from 'react';
import Header from '@components/Header/Header';
import PartesSubHeader from './BBs/subheader/subheader';
import EsfirusPartesTable from './BBs/table/Table';
import EsfirusButtonNative from '@components/ui/Button/EsfirusButtonNative';
import backIcon from '@assets/images/icons/back-arrow.svg';
import pendingIcon from '@assets/svgs/pending-icon.svg';
import { useNavigate } from 'react-router-dom';
import './Pending.scss';
import { useDispatch, useSelector } from 'react-redux';
import { deleteReport } from '@store/slices/reports';
import { emptyResourceRedirect } from '@helpers/navigate';
import usePartesGet from '@services/hooks/usePartesGet';
import { Empresa, Parte } from '@appTypes/common';
import { sortEmpleados } from '@helpers/utils';
import { EsfirusTablePropsPagination, HandleChangePaginationType } from '@services/hooks/useHandlerPaginationTable';
import { Box } from '@mui/material';
import Loader from '@components/ui/Loader/Loader';
import EsfirusResumeAsync from '@components/ui/ResumeAsync/EsfirusResumeAsync';
import { TABLE_PARTES_VALIDATING_ID } from '@store/slices/tableFilters';
import { isEmpleado } from '@services/hooks/UserHook';
import { usersService } from '@services/users';
import { RootState } from '@store/index';

type EmployeesList = { label: string; value: string };

function Pending() {
  const { listValidating: partes = [], loadingValidating, totalItemsValidating } = useSelector((state: RootState) => state.partes);

  const getPartes = usePartesGet();
  const navigate = useNavigate();
  const empresas = useSelector((state) => (state as any).empresas).list;
  const dispatch = useDispatch();
  const customFilters = useSelector((state) => (state as any).tableFilters[TABLE_PARTES_VALIDATING_ID]);
  const configuration = useSelector((state) => (state as any).configurations.config);

  const filtersToApi = useMemo(() => ({
    empresa: customFilters?.selectedFilters?.empresa,
    obra: customFilters?.selectedFilters?.obra,
    dateFrom: customFilters?.selectedFilters?.dateFrom,
    dateTo: customFilters?.selectedFilters?.dateTo,
    search: customFilters?.searchQuery,
    empleado: customFilters?.selectedFilters?.empleado,
    responsable: customFilters?.selectedFilters?.responsable,
  }), [customFilters]);

  const sortToApi = useMemo(() => {
    let sort: Array<string> = [];

    if (customFilters?.sortTable) {
      sort = [customFilters.sortTable.field.keymap, customFilters.sortTable.ascending ? 'asc' : 'desc'];
    }

    return sort;
  }, [customFilters?.sortTable]);

  const [pendingSelected, setPendingSelected] = useState({});
  const [seletedAll, setSeletedAll] = useState(false);
  const [empleadosList, setEmpleadosList] = useState<any>([]);
  const [responsablesList, setResponsablesList] = useState<any>([]);
  const [pagination, setPagination] = useState<EsfirusTablePropsPagination>({ page: 0, sizePage: 50 });

  const rol = localStorage.getItem('ef-user-rol');

  useEffect(() => {
    emptyResourceRedirect({
      check: empresas.length === 0,
      path: '/partes/pendientes',
      navigate,
    });
  }, [empresas, navigate]);

  useEffect(() => {
    dispatch(getPartes({ offset: pagination.page, limit: pagination.sizePage, filtersValidating: filtersToApi, sortValidating: sortToApi } as any) as any);
  }, [pagination, filtersToApi, sortToApi]);

  useEffect(() => {
    fetchEmpleadosAndResponsables()
  }, [partes, customFilters?.selectedFilters, configuration]);

  const fetchEmpleadosAndResponsables = async () => {
    const months = (isEmpleado() ? configuration?.listado?.listadoParteMeses : configuration?.listado?.listadoParteMesesAdmin) ?? 1;

    try {
      const resp = await usersService.getListByPartesFilterValidating({ filters: { ...customFilters?.selectedFilters, months } });

      const empleados = sortEmpleados(resp?.data?.data?.empleados ?? []);
      const responsables = sortEmpleados(resp?.data?.data?.responsables ?? []);

      const empleadosWithData = empleados.map((empleado: any) => ({ ...empleado, data: JSON.parse(empleado.data) }));
      const responsablesWithData = responsables.map((empleado: any) => ({ ...empleado, data: JSON.parse(empleado.data) }));

      setEmpleadosList(empleadosWithData);
      setResponsablesList(responsablesWithData);
    } catch (error) {
      setEmpleadosList([]);
    }
  }

  useEffect(() => {
    setPendingSelected({});
  }, [partes]);

  const deleteParte = (id: any) => {
    dispatch(deleteReport(id) as any).then(() =>
      dispatch(getPartes({ offset: 0, limit: 50, filtersValidating: filtersToApi, sort: sortToApi } as any) as any)
    );
  };

  const handleChangePagination: HandleChangePaginationType = (newPaginationData) => {
    setPagination(newPaginationData)
  }

  const parteslistOrdered = useMemo(() => {
    return partes?.slice().sort((a: any, b: any) => b.date - a.date);
  }, [partes]);

  return (
    <>
      <Header />
      <div className="center">
        <div className="header-pending">
          <EsfirusButtonNative
            className={'tertiary'}
            label={window.innerWidth > 500 ? 'Volver' : ''}
            icon={backIcon}
            iconClass={'icon-tertiary'}
            click={() => {
              navigate('/partes');
            }}
          ></EsfirusButtonNative>

          <div className="title">
            <div className="pending-title-container">
              <img src={pendingIcon} alt="pendingIcon" srcSet="" />
              <h4 className="pending-title">Pendiente de validar</h4>
            </div>
            <h5>Selecciona el parte que quieres validar</h5>
          </div>
        </div>

        <PartesSubHeader
          partes={parteslistOrdered}
          pendingSelected={pendingSelected}
          empleadosList={empleadosList}
          responsablesList={responsablesList}
        />

        <EsfirusPartesTable
          tableId={TABLE_PARTES_VALIDATING_ID}
          seletedAll={seletedAll}
          pendingSelected={pendingSelected}
          totalItems={totalItemsValidating}
          pagination={pagination}
          handleChangePagination={handleChangePagination}
          partes={parteslistOrdered as any}
          loading={loadingValidating}
          deleteReport={deleteParte}
          empresas={empresas as any}
          rol={rol as any}
          onCheckRow={(row: { row: string; value: boolean }) => {
            if (!row.value) {
              setSeletedAll(false);
            }

            const newSelection = { ...pendingSelected };
            (newSelection as any)[row.row] = row.value;
            setPendingSelected(newSelection as never);
          }}
          onCheckAll={() => {
            setSeletedAll(!seletedAll);
            const newSelection = {};
            parteslistOrdered.forEach((parte: any) => {
              (newSelection as any)[parte.id] = !seletedAll;
            });
            setPendingSelected(newSelection as never);
          }}
          onOpenEdit={(opened: any) => {
            setSeletedAll(false);
            const newSelection = {};
            (newSelection as any)[opened.id] = true;
            setPendingSelected(newSelection as never);
          }}
        >
          <Box className="ml-20 mr-20 mt-40 mb-40">
            <Loader message="Cargando partes de validación" />
          </Box>
        </EsfirusPartesTable>
        <EsfirusResumeAsync validating={true} />
      </div>
    </>
  );
}

export default Pending;
